import pillTemplate from './pill.html?raw';

export class Pill extends HTMLElement {
    static get observedAttributes() {
        return ['small', 'value', 'removable', 'disabled', 'aria-label-text'];
    }

    constructor() {
        super();

        this.currentElement = null;
    }

    connectedCallback() {
        this.attachShadow({ mode: 'open' });

        const template = document.createElement('template');
        template.innerHTML = pillTemplate;
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        const pill = this.shadowRoot.querySelector('.pill');

        if (!pill) return;

        this.currentElement = pill;

        if (this.isDisabled) {
            pill.classList.add('disabled');
        }

        if (this.isSmall) {
            pill.classList.add('small');
        }

        if (this.isRemovable) {
            this.switchElement(pill, 'button');
        } else {
            pill.classList.remove('removable');
        }

        if (this.hasAttribute('value')) {
            pill.setAttribute('data-value', this.getAttribute('value'));
        }

        if (this.hasAttribute('aria-label')) {
            pill.setAttribute('aria-label', this.getAttribute('aria-label'));
        }
    }

    attributeChangedCallback(name, _, newValue) {
        if (this.shadowRoot === null) return;

        const pill = this.shadowRoot.querySelector('.pill');
        if (!pill) return;

        const isButton = pill.tagName.toLowerCase() === 'button';

        switch (name) {
            case 'removable':
                if (newValue === null || newValue === 'false') {
                    if (isButton) {
                        this.switchElement();
                    }
                } else {
                    if (!isButton) {
                        this.switchElement();
                    }
                }
                break;
            case 'disabled':
                if (newValue !== null && newValue !== 'false') {
                    pill.classList.add('disabled');
                    if (isButton) {
                        pill.setAttribute('disabled', 'true');
                    }
                } else {
                    pill.classList.remove('disabled');
                    if (isButton) {
                        pill.removeAttribute('disabled');
                    }
                }
                break;
            case 'size':
                if (this.isSmall && !pill.classList.contains('small')) {
                    pill.classList.add('small');
                } else if (!this.isSmall) {
                    pill.classList.remove('small');
                }
                break;
            case 'aria-label-text':
                if (newValue !== null && isButton) {
                    pill.setAttribute('aria-label', newValue);
                } else {
                    pill.removeAttribute('aria-label');
                }
        }
    }

    get isDisabled() {
        return (
            this.hasAttribute('disabled') &&
            this.getAttribute('disabled') !== 'false'
        );
    }

    get isSmall() {
        return (
            this.hasAttribute('small') && this.getAttribute('small') !== 'false'
        );
    }

    get isRemovable() {
        return (
            this.hasAttribute('removable') &&
            this.getAttribute('removable') !== 'false'
        );
    }

    switchElement() {
        const fromElement = this.currentElement;
        const toTagName =
            fromElement.tagName.toLowerCase() === 'button' ? 'div' : 'button';

        const newElement = document.createElement(toTagName);

        this.cloneAttributes(fromElement, newElement);
        this.moveChildNodes(fromElement, newElement);

        fromElement.parentNode.replaceChild(newElement, fromElement);

        if (toTagName === 'button') {
            newElement.classList.add('pill', 'removable');
            newElement.setAttribute('type', 'button');

            if (this.hasAttribute('aria-label-text')) {
                newElement.setAttribute(
                    'aria-label',
                    this.getAttribute('aria-label-text')
                );
            }

            if (this.isDisabled) {
                newElement.setAttribute('disabled', '');
            }

            newElement.addEventListener('click', () => {
                if (
                    newElement.hasAttribute('disabled') &&
                    newElement.getAttribute('disabled') !== 'false'
                ) {
                    return;
                }

                this.dispatchEvent(
                    new CustomEvent('pill:remove', { bubbles: true })
                );
            });
        } else {
            newElement.setAttribute('role', 'text');
        }

        this.currentElement = newElement;
    }

    cloneAttributes(source, target) {
        Array.from(source.attributes).forEach((attr) => {
            target.setAttribute(attr.name, attr.value);
        });
    }

    moveChildNodes(source, target) {
        while (source.firstChild) {
            target.appendChild(source.firstChild);
        }
    }
}

customElements.define('svea-pill', Pill);
